<script>
	import { page } from "$app/stores";
	import { goto } from "$app/navigation";

	import { doc, getFirestore, setDoc } from "firebase/firestore";
	import { formattedPhone, slugify, watchObject } from "cando-util";
	import { isValidPhoneNumber } from "libphonenumber-js";

	import { store } from "$lib/utilities/store";
	import { translate } from "$lib/utilities/translate";
	import { fileToMarkdown } from "$lib/utilities/fileToMarkdown";
	import { getDownloadUrl } from "$lib/utilities/getDownloadUrl.js";
	import { formatHeading } from "$lib/sections/Admin/tableColumnsUtil.js";
	import { recordUpdate } from "./recordUpdate.js";
	import { formUpdate } from "$lib/sections/Admin/formUpdate";
	import {
		createSetting,
		parseTabOrder,
		scheduleMissingSettingUpdate,
	} from "./dashboardSettings.js";

	import ScreenSize from "$lib/components/ScreenSize.svelte";
	import PhoneNumber from "$lib/components/PhoneNumber.svelte";
	import Tabs from "$lib/components/Tabs.svelte";
	import Dashboard from "./Dashboard.svelte";
	import SettingsFormInput from "../SettingsFormInput.svelte";
	// p-4 grid grid-cols-1 gap-2 md:grid-cols-2 sm:gap-4 bg-base-100 text-success

	export let dbGroup;

	let lang;
	$: lang = $page.data.lang;

	$: watchObject(getDownloadUrl, "getDownloadUrl");

	$: watchObject(recordUpdate, "recordUpdate");
	$: watchObject(formUpdate, "formUpdate");
	$: watchObject(formatHeading, "formatHeading");
	$: watchObject(fileToMarkdown, "fileToMarkdown");
	const getDoc = (docId, col) => $store?.[col]?.docs?.[docId] || null;
	$: watchObject(getDoc, "getDoc");
	$: watchObject($store.userProfile, "user");

	$: tabsValue = translate($store?.settings?.docs?.[dbGroup]?.tabs, lang);
	$: watchObject(tabsValue, "tabsValue");

	let tabOrder = [];
	let tabUpdateTimeout;
	let loading = true;

	$: if (tabsValue) {
		if (tabUpdateTimeout) clearTimeout(tabUpdateTimeout);
		tabUpdateTimeout = setTimeout(() => {
			tabOrder = parseTabOrder(tabsValue);
		}, 1000);
	}

	let settingUpdateTimeout;
	let tabsSettingInitializeTimeout;

	const settingUpdate = (event) => {
		if (settingUpdateTimeout) clearTimeout(settingUpdateTimeout);
		settingUpdateTimeout = setTimeout(async () => {
			await formUpdate(event, $store, loading, "settings", dbGroup);
		}, 1000);
	};

	$: if (
		dbGroup &&
		$store?.userRoles?.admin &&
		$store?.settings?.docs &&
		$store.settings.docs?.[dbGroup]?.tabs === undefined
	) {
		if (tabsSettingInitializeTimeout)
			clearTimeout(tabsSettingInitializeTimeout);
		const settingId = dbGroup;
		tabsSettingInitializeTimeout = scheduleMissingSettingUpdate({
			getCurrentSetting: () => $store.settings.docs?.[settingId]?.tabs,
			onMissing: () =>
				recordUpdate("settings", settingId, {
					tabs: createSetting(),
				}),
		});
	}

	let screenWidth;
	let breakpoint;

	$: leftMargin = screenWidth > 1440 ? (screenWidth - 1440) / 2 : 0;

	$: url = $page.url;
	$: selectedTab = url.searchParams.get("tab") || tabOrder.at(0) || null;
	$: lang = window.location.pathname.includes(`/${$page.data.lang}/`)
		? $page.data.lang
		: "";

	let phoneNumber = $store.userProfile.phone;
	let wantsTextNotifications =
		$store.userProfile.wantsTextNotificationsForSubmissions;
	let isSavingNotificationPreference = false;
	let notificationStatus = "";

	const handleNotificationPreferencesSubmit = async () => {
		const phone = document.getElementById(
			"notification-phone-input-value",
		).value;
		if (!isValidPhoneNumber(phone)) {
			notificationStatus = "Please enter a valid phone number.";
			return;
		}
		isSavingNotificationPreference = true;
		notificationStatus = "";
		try {
			await setDoc(
				doc(getFirestore(), `profiles/${$store.userProfile.uid}`),
				{
					phone,
					wantsTextNotificationsForSubmissions:
						wantsTextNotifications,
				},
				{ merge: true },
			);
			notificationStatus = wantsTextNotifications
				? `We'll send texts to ${formattedPhone(phone, false)}.`
				: "Text notifications disabled for now.";
		} catch (error) {
			console.error("Failed to update notification preferences", error);
			notificationStatus =
				"We couldn't update your notification preference. Try again.";
		} finally {
			isSavingNotificationPreference = false;
		}
	};

	let previousSelectedTab = null;
	$: if (selectedTab !== previousSelectedTab) {
		if (previousSelectedTab !== null) {
			updateSearchParams({
				tab: selectedTab,
				docId: null,
			});
		}
		previousSelectedTab = selectedTab;
	}

	const updateSearchParams = async (updates = {}) => {
		const params = new URLSearchParams(url.search);
		Object.entries(updates).forEach(([key, value]) => {
			if (value === undefined) return;
			if (value === null || value === "") {
				params.delete(key);
			} else {
				params.set(key, value);
			}
		});
		const query = params.toString();
		const destination = `${url.pathname}${query ? `?${query}` : ""}`;
		const current = `${url.pathname}${url.search}`;
		if (destination === current) return;
		await goto(destination, { replaceState: true, noScroll: true });
	};
</script>

<ScreenSize bind:width={screenWidth} bind:breakpoint showBreakpoint={false} />

<div class="max-w-360 px-2 sm:px-4" style={`margin-left: ${leftMargin}px;`}>
	<div class="w-full space-y-4">
		<!-- <h1
			class="text-center text-2xl sm:text-3xl markdown my-5 sm:my-10 uppercase"
		>
			Case Management
		</h1> -->

		<!-- <p class="mx-auto max-w-160 text-center">
			Quickly review intake activity, contact records, and active cases in
			a single workspace. Switch between tabs to pull up the latest data
			from each collection without leaving the admin panel.
		</p> -->

		<div class="w-full space-y-6">
			<Tabs
				id={`${dbGroup}-management-tabs-scroll-container`}
				bind:selectedTab
				tabs={tabOrder.map((section) => ({
					id: section,
					label: section,
					class: selectedTab === section ? "" : "opacity-70",
				}))}
			/>
		</div>

		{#if selectedTab}
			<Dashboard
				col={slugify(selectedTab)}
				settingSuffix={selectedTab}
				{dbGroup}
			/>
		{/if}

		{#if $store?.userRoles?.admin}
			<div class="shadow-xl collapse collapse-arrow bg-base-300">
				<input type="checkbox" />
				<div class="font-medium collapse-title">Available Tabs</div>
				<div class="collapse-content">
					{#if $store?.settings?.docs?.[dbGroup]?.[`tabs`]}
						<SettingsFormInput
							col={"settings"}
							settingId={dbGroup}
							fld={{
								key: `tabs`,
								...$store?.settings?.docs?.[dbGroup]?.[`tabs`],
							}}
							{lang}
							handleChange={settingUpdate}
						/>
					{/if}
				</div>
			</div>
		{/if}

		<div class="flex justify-center">
			<form
				on:submit|preventDefault={handleNotificationPreferencesSubmit}
				class="max-w-md space-y-4 rounded-lg bg-base-300 p-4 shadow"
			>
				<div class="form-control">
					<label class="label" for="notification-phone-input">
						<span class="label-text text-sm font-semibold"
							>Phone Number</span
						>
					</label>
					<PhoneNumber
						bind:value={phoneNumber}
						label="notification phone"
						handleChange={() => (notificationStatus = "")}
						disabled={isSavingNotificationPreference}
						key="notification-phone-input"
					/>
				</div>

				<label
					class="flex items-center gap-3 text-sm w-full text-nowrap"
				>
					<input
						type="checkbox"
						id={`${slugify($store.userProfile.uid)}-${slugify("wantsTextNotificationsForSubmissions")}-checkbox-input`}
						class="checkbox checkbox-primary"
						bind:checked={wantsTextNotifications}
					/>
					<span>Send me text notifications about Submissions.</span>
				</label>

				<button
					type="submit"
					class="btn btn-primary"
					disabled={isSavingNotificationPreference}
				>
					{isSavingNotificationPreference
						? "Saving preferences..."
						: "Save notification preference"}
				</button>

				{#if notificationStatus}
					<p class="text-sm text-base-content">
						{notificationStatus}
					</p>
				{/if}
			</form>
		</div>
	</div>
</div>
